import type { ActionCodeSettings, User } from "firebase/auth"
import { sendEmailVerification } from "firebase/auth"

// continue URL（actionCodeSettings.url）のドメインが Firebase の承認済みドメインに
// 未登録・不正なときに返るエラーコード群。これらのときは continue URL 無しで再送する。
const CONTINUE_URI_ERROR_CODES = new Set([
  "auth/unauthorized-continue-uri",
  "auth/invalid-continue-uri",
  "auth/missing-continue-uri",
])

/**
 * メール確認を送信する。
 *
 * `actionCodeSettings.url` のドメインが Firebase の承認済みドメインに未登録だと
 * `auth/unauthorized-continue-uri` で失敗し、確認メールが一切送られない（#741）。
 * その場合は continue URL 無しでフォールバック送信し、少なくとも確認メールは届くようにする。
 *
 * どのケースでも例外を投げない。呼び出し時点でユーザー登録自体は成立しており、
 * ここで throw すると登録フローを止めたり、未処理拒否として Sentry に error を
 * 送ってしまうため（元実装は await されない floating promise だった）。
 *
 * @returns 送信できたら true、最終的に失敗したら false
 */
export const sendVerificationEmail = async (
  user: User,
  actionCodeSettings?: ActionCodeSettings,
): Promise<boolean> => {
  try {
    await sendEmailVerification(user, actionCodeSettings ?? null)
    return true
  } catch (error) {
    const code =
      typeof error === "object" && error !== null && "code" in error
        ? String((error as { code: unknown }).code)
        : ""

    // continue URL 起因のエラーなら、URL 無し（Firebase 既定ドメイン）で再送
    if (actionCodeSettings && CONTINUE_URI_ERROR_CODES.has(code)) {
      try {
        await sendEmailVerification(user)
        return true
      } catch {
        // フォールバックも失敗 → 下の warn へ
      }
    }

    // 登録は成立しているのでログのみ（Sentry には error として送らない）
    console.warn(
      `sendVerificationEmail failed: ${code || "unknown error"}`,
    )
    return false
  }
}
